import { useCallback, useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '@/components/Button';
import { TextField } from '@/components/TextField';

/**
 * Password for the social media page. Set `VITE_SOCIAL_PAGE_PASSWORD` in
 * `.env.local`; falls back to a dev default so the page still opens locally.
 *
 * NOTE: this is a client-side gate only — the password ships in the bundle, so
 * it keeps casual visitors out, it is not real security.
 */
const PASSWORD = import.meta.env.VITE_SOCIAL_PAGE_PASSWORD?.trim()
  ? import.meta.env.VITE_SOCIAL_PAGE_PASSWORD
  : 'stackly';

const STORAGE_KEY = 'stackly:social-media-unlocked';

const readUnlocked = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Renders `children` only once the correct password has been entered. The
 * unlock lasts for the browser session (cleared when the tab closes).
 */
export const PasswordGate = ({ children }: { children: ReactNode }) => {
  const [unlocked, setUnlocked] = useState(readUnlocked);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const submit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (value !== PASSWORD) {
        setError('Incorrect password.');
        setValue('');
        return;
      }
      try {
        sessionStorage.setItem(STORAGE_KEY, '1');
      } catch {
        // Storage unavailable (private mode) — unlock for this render only.
      }
      setUnlocked(true);
    },
    [value],
  );

  if (unlocked) return <>{children}</>;

  return (
    <form onSubmit={submit} className="mx-auto flex w-full max-w-sm flex-col gap-4 py-16">
      <div>
        <h1 className="text-h2 font-semibold text-ink">Password required</h1>
        <p className="mt-1 text-body text-warm-500">
          Enter the password to open the social media poster.
        </p>
      </div>
      <TextField
        label="Password"
        type="password"
        autoComplete="current-password"
        value={value}
        error={error}
        onChange={(event) => {
          setValue(event.target.value);
          setError(undefined);
        }}
      />
      <Button type="submit" fullWidth>
        Unlock
      </Button>
    </form>
  );
};
